import { PageObject, PAGEOBJECTTYPE } from "./pageobject";
import { PageObjectContainer } from "./pageobjectcontainer";
import { PageObjectButton } from "./pageobjectbutton";
import { PageObjectEvaluation } from "./pageobjectevaluation";
import { PageObjectImage } from "./pageobjectimage";
import { PageObjectInput } from "./pageobjectinput";
import { PageObjectSelection } from "./pageobjectselection";
import { PageObjectSound } from "./pageobjectsound";
import { PageObjectText } from "./pageobjecttext";
import { PageObjectLink } from "./pageobjectlink";
import { PageObjectList } from "./pageobjectlist";
import { PageObjectUploadButton } from "./pageobjectuploadbutton";
import { PageObjectVideo } from "./pageobjectvideo";
import { PageObjectArrow } from "./pageobjectarrow";
import { Utils } from "./utils";

export class Page {
  name: string;
  background: string;
  pageIcon: string;
  objects: PageObject[];

  constructor(page: Page = null, style: string = "") {
    if (page == null) {
      this.name = "New Page";
      this.objects = [];
    } else {
      this.name = page.name;
      this.background = page.background || "";
      this.pageIcon = page.pageIcon || "";
      this.objects = [];
      page.objects.forEach((obj: any) => {
        this.duplicateAndAddObject(obj, false, style);
      });
      this.sortObjects();
    }
  }

  duplicateAndAddObject(obj: PageObject, rename: boolean = true, style: string = ""): PageObject {
    let ret: PageObject = Utils.duplicateObject(obj, this, style);
    if (ret == null) {
      return null;
    }
    this.objects.push(ret);

    if (rename) {
      let num = 0;
      let found;
      do {
        found = false;
        ret.name = obj.name + (num > 0 ? "_" + num : "");
        if (this.checkObjectNameInUse(ret.name, ret)) {
          found = true;
        }
        num++;
      } while (found);
    }

    this.sortObjects();
    return ret;
  }

  sortObjects() {
    this.objects.sort((obj1, obj2) => {
      if (obj1.z > obj2.z) {
        return 1;
      }
      if (obj1.z < obj2.z) {
        return -1;
      }
      return 0;
    });
    for (let i = 0; i < this.objects.length; i++) {
      if (this.objects[i].type == PAGEOBJECTTYPE.Container) {
        (<PageObjectContainer>this.objects[i]).sortObjects();
      }
    }
  }

  private checkObjectNameInUseFromObjects(objects: PageObject[], name: string, exceptObj: PageObject): boolean {
    for (let i = 0; i < objects.length; i++) {
      if (objects[i] != exceptObj && objects[i].name == name) {
        return true;
      }
      if (objects[i].type == PAGEOBJECTTYPE.Container) {
        let container = <PageObjectContainer>objects[i];
        let ret = this.checkObjectNameInUseFromObjects(container.objects, name, exceptObj);
        if (ret) {
          return true;
        }
      }
    }

    return false;
  }

  checkObjectNameInUse(name: string, exceptObj: PageObject = null): boolean {
    return this.checkObjectNameInUseFromObjects(this.objects, name, exceptObj);
  }

  createPageObjectContainer(style: string) {
    let obj = new PageObjectContainer(null, null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "Container " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }

  createPageObjectButton(style: string) {
    let obj = new PageObjectButton(null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "Button " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }

  createPageObjectEvaluation(style: string) {
    let obj = new PageObjectEvaluation(null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "Evaluation " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }

  createPageObjectImage(style: string) {
    let obj = new PageObjectImage(null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "Image " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }

  createPageObjectInput(style: string) {
    let obj = new PageObjectInput(null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "Input " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }

  createPageObjectArrow(style: string) {
    let obj = new PageObjectArrow(null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "Arrow " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }

  createPageObjectSelection(style: string) {
    let obj = new PageObjectSelection(null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "Selection " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }

  createPageObjectSound(style: string) {
    let obj = new PageObjectSound(null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "Sound " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }

  createPageObjectText(style: string) {
    let obj = new PageObjectText(null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "Text " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }

  createPageObjectLink(style: string) {
    let obj = new PageObjectLink(null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "Link " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }

  createPageObjectList(style: string) {
    let obj = new PageObjectList(null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "List " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }

  createPageObjectUploadButton(style: string) {
    let obj = new PageObjectUploadButton(null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "UploadButton " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }

  createPageObjectVideo(style: string) {
    let obj = new PageObjectVideo(null, style);

    let num = 1;
    let found;
    do {
      found = false;
      obj.name = "Video " + num;
      if (this.checkObjectNameInUse(obj.name)) {
        found = true;
      }
      num++;
    } while (found);

    this.objects.push(obj);
    this.sortObjects();
  }
}
